// Copyright (C) 2020-2022 Intel Corporation
//
// SPDX-License-Identifier: MIT

import React, { useCallback, useRef } from 'react';

import { LabelType } from 'cvat-core-wrapper';
import { t } from 'utils/i18n';
import LabelForm from './label-form';
import { LabelOptColor, SkeletonConfiguration } from './common';
import SkeletonConfigurator from './skeleton-configurator';
import ConstructorViewer from './constructor-viewer';

interface Props {
    label: LabelOptColor;
    labelNames: string[];
    onUpdate: (label: LabelOptColor) => void;
    onCancel: () => void;
    onSubUpdate?: (label: LabelOptColor) => void;
    onSubDelete?: (label: LabelOptColor) => void;
    onSubCreate?: () => void;
}

function ConstructorUpdater(props: Props): JSX.Element {
    const {
        label, labelNames, onUpdate, onCancel, onSubUpdate, onSubDelete, onSubCreate,
    } = props;
    const { type } = label;
    const skeletonConfiguratorRef = useRef<SkeletonConfigurator>(null);

    const onSkeletonSubmit = useCallback((): SkeletonConfiguration | null => {
        if (skeletonConfiguratorRef.current) {
            return skeletonConfiguratorRef.current.submit();
        }

        return null;
    }, [skeletonConfiguratorRef]);

    const resetSkeleton = useCallback((): void => {
        if (skeletonConfiguratorRef.current) {
            skeletonConfiguratorRef.current.reset();
        }
    }, [skeletonConfiguratorRef]);

    return (
        <>
            <div className='cvat-label-constructor-updater'>
                <LabelForm
                    key={JSON.stringify(label)}
                    label={label}
                    labelNames={labelNames}
                    onSubmit={onUpdate}
                    resetSkeleton={type === 'skeleton' ? resetSkeleton : undefined}
                    onSkeletonSubmit={type === 'skeleton' ? onSkeletonSubmit : undefined}
                    onCancel={onCancel}
                />
                {type === LabelType.SKELETON && (
                    <SkeletonConfigurator
                        ref={skeletonConfiguratorRef}
                        label={label}
                        disabled={(label.id as number) > 0}
                    />
                )}
            </div>

            {!label.has_parent && label.id > 0 && (
                <div style={{ marginTop: 26 }}>
                    <div className='ant-typography cvat-text-color'>
                        <strong>{t('Sub-labels')}</strong>
                    </div>

                    <ConstructorViewer
                        style={{ border: 'none', minHeight: 'auto', padding: '0' }}
                        labels={label.sublabels}
                        onUpdate={onSubUpdate}
                        onDelete={onSubDelete}
                        onCreate={onSubCreate}
                        onlyCreate
                    />
                </div>
            )}
        </>
    );
}

export default React.memo(ConstructorUpdater);
